import type { ChatAttachmentUpload } from '@newfront-insurance/ai-api';
import { Button, cn, Input } from '@newfront-insurance/core-ui/v2';
import { ArrowLeft, ArrowUp } from 'lucide-react';
import { useState, useSyncExternalStore } from 'react';
import { v4 } from 'uuid';

import type { FileAttachment } from '../../../../hooks/use-file-selection';
import { readyUploads } from '../../../../lib/chat-workspace';
import {
  getBrowserTimezone,
  NO_ACCOUNT_SELECTED_SCHEDULE_LABEL,
  validateNovaScheduleProposalDraft,
  type NovaScheduleOutputPolicy,
  type NovaScheduleProposalDraft,
  type NovaScheduleRecurrence,
} from '../../../../lib/nova-schedule-proposal';
import { AccountSelector, type SelectedAccount } from '../../../account-selector';
import { novaGradientClass, novaGradientStateClass } from '../../design-system/gradients';
import { useUnsavedNovaChanges } from '../../navigation/use-unsaved-nova-changes';
import {
  NovaPromptEditor,
  hasUnsettledNovaPromptAttachments,
  mergeNovaPromptFiles,
  novaPromptFileAttachments,
  type NovaPromptFile,
} from '../chat-input/nova-prompt-editor';
import { NovaScheduleDeliveryEditor, NovaScheduleWhenEditor } from '../tools/nova-schedule-proposal-fields';

const SCHEDULE_TEMPLATES = [
  {
    title: 'Weekly account brief',
    prompt:
      'Every Monday at 8am, prep a weekly account brief covering recent claims, invoices, policy changes, and document activity. Highlight anything renewal-relevant.',
  },
  {
    title: 'Renewal countdown',
    prompt:
      'Every Friday at 9am, summarize accounts renewing in the next 90 days and flag missing applications, quotes, or decisions.',
  },
  {
    title: 'Claims digest',
    prompt: 'Every weekday at 8am, summarize new claims activity, reserve changes, and anything that needs follow-up.',
  },
  {
    title: 'Document changes',
    prompt:
      'Every Friday at 3pm, summarize important changes from documents added this week and call out anything time-sensitive.',
  },
] as const;

type ScheduleCreateMode = 'prompt' | 'manual';

export interface ManualNovaScheduleCreateInput {
  account: SelectedAccount;
  title: string;
  prompt: string;
  attachmentUploads: ChatAttachmentUpload[];
  timezone: string;
  startAt?: string;
  recurrence: NovaScheduleRecurrence;
  outputPolicy?: NovaScheduleOutputPolicy;
}

interface NovaScheduleCreateViewProps {
  showBack: boolean;
  loggedInUserUuid: string;
  userScopes: string[];
  isSaving: boolean;
  error?: unknown;
  onBack: () => void;
  /** Starts the chat that drafts the schedule: `chatUuid` is the chat the files were uploaded for. */
  onPromptSubmit: (prompt: string, files: { chatUuid: string; attachmentUploads: ChatAttachmentUpload[] }) => void;
  onManualSubmit: (input: ManualNovaScheduleCreateInput) => Promise<void> | void;
}

const subscribeToStaticBrowserTimezone = () => () => undefined;

export function NovaScheduleCreateView({
  showBack,
  loggedInUserUuid,
  userScopes,
  isSaving,
  error,
  onBack,
  onPromptSubmit,
  onManualSubmit,
}: NovaScheduleCreateViewProps) {
  const [mode, setMode] = useState<ScheduleCreateMode>('prompt');
  const browserTimezone = useSyncExternalStore(
    subscribeToStaticBrowserTimezone,
    () => getBrowserTimezone(),
    () => 'UTC',
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6 bg-white">
      {showBack ? (
        <button
          type="button"
          onClick={onBack}
          className="text-brand-400 inline-flex w-fit items-center gap-1 text-[14px] leading-5 font-semibold"
        >
          <ArrowLeft aria-hidden="true" className="size-4" />
          Scheduled
        </button>
      ) : null}

      <div className="flex items-center justify-between gap-4">
        <h2 className="text-steel-500 text-[17px] leading-6">New schedule</h2>
        <ScheduleCreateModeSwitch value={mode} onChange={setMode} />
      </div>

      {mode === 'prompt' ? (
        <PromptScheduleCreate isSaving={isSaving} onSubmit={onPromptSubmit} />
      ) : (
        <ManualScheduleCreate
          key={browserTimezone}
          timezone={browserTimezone}
          loggedInUserUuid={loggedInUserUuid}
          userScopes={userScopes}
          isSaving={isSaving}
          error={error}
          onCancel={showBack ? onBack : () => setMode('prompt')}
          onSubmit={onManualSubmit}
        />
      )}
    </div>
  );
}

function ScheduleCreateModeSwitch({
  value,
  onChange,
}: {
  value: ScheduleCreateMode;
  onChange: (mode: ScheduleCreateMode) => void;
}) {
  return (
    <div className="border-steel-75 flex rounded-full border p-1" aria-label="Schedule creation method">
      {(
        [
          ['prompt', 'Prompt with Nova'],
          ['manual', 'Manual'],
        ] as const
      ).map(([mode, label]) => (
        <button
          key={mode}
          type="button"
          aria-pressed={value === mode}
          onClick={() => onChange(mode)}
          className={cn(
            'text-steel-500 rounded-full px-3 py-1 text-[12px] leading-4 transition-colors',
            value === mode && 'bg-steel-50',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function PromptScheduleCreate({
  isSaving,
  onSubmit,
}: {
  isSaving: boolean;
  onSubmit: (prompt: string, files: { chatUuid: string; attachmentUploads: ChatAttachmentUpload[] }) => void;
}) {
  const [prompt, setPrompt] = useState('');
  const [promptFiles, setPromptFiles] = useState<NovaPromptFile[]>([]);
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  // The chat that drafts the schedule: files upload into its workspace before it starts.
  const [chatUuid] = useState(() => v4());
  const mergedFiles = mergeNovaPromptFiles(promptFiles, attachments);
  const hasUnsettledAttachments = hasUnsettledNovaPromptAttachments(attachments);
  const canSubmit = prompt.trim().length > 0 && !isSaving && !hasUnsettledAttachments;
  useUnsavedNovaChanges(Boolean(prompt.trim()) || promptFiles.length > 0 || attachments.length > 0);

  const submit = () => {
    if (canSubmit)
      onSubmit(prompt.trim(), {
        chatUuid,
        attachmentUploads: novaPromptFileAttachments(mergedFiles, readyUploads(attachments)).attachmentUploads,
      });
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-4">
        <div>
          <h3 className="text-steel-500 text-[17px] leading-6 font-semibold">Have Nova work for you, automatically.</h3>
          <p className="text-steel-400 mt-4 text-[12px] leading-4">
            Describe the outcome and cadence. Nova will turn it into a schedule for you to review before anything is
            created.
          </p>
        </div>

        <div className="relative">
          <NovaPromptEditor
            value={prompt}
            existingFiles={promptFiles}
            attachments={attachments}
            disabled={isSaving}
            placeholder="e.g. Every Monday at 9am, prep a renewal brief for Cactus Plants covering claims, quotes, and document changes"
            onChange={setPrompt}
            onExistingFilesChange={setPromptFiles}
            setAttachments={setAttachments}
            chatUuid={chatUuid}
          />
          <button
            type="button"
            aria-label="Start creating schedule"
            disabled={!canSubmit}
            onClick={submit}
            className={cn(
              'absolute right-3 bottom-3 z-10 flex size-8 items-center justify-center rounded-full transition-opacity',
              canSubmit ? novaGradientClass.aiBackground : 'bg-steel-75 text-steel-200',
            )}
          >
            <ArrowUp aria-hidden="true" className="size-[18px]" />
          </button>
        </div>
      </div>

      <section className="flex flex-col gap-3" aria-labelledby="schedule-template-heading">
        <h3 id="schedule-template-heading" className="text-steel-500 text-[14px] leading-5">
          Schedule templates
        </h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {SCHEDULE_TEMPLATES.map((template) => (
            <button
              key={template.title}
              type="button"
              onClick={() => setPrompt(template.prompt)}
              className={cn(
                'bg-steel-75 flex min-h-[110px] rounded-[5px] p-px text-left transition-colors',
                'focus-visible:outline-hidden',
                novaGradientStateClass.aiBorderIconHover,
                novaGradientStateClass.aiBorderIconFocusVisible,
              )}
            >
              <span className="flex flex-1 flex-col gap-2 rounded-[4px] bg-white p-[15px]">
                <span className="text-steel-500 text-[14px] leading-5 font-semibold">{template.title}</span>
                <span className="text-steel-400 text-[12px] leading-4">{template.prompt}</span>
              </span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

function ManualScheduleCreate({
  timezone,
  loggedInUserUuid,
  userScopes,
  isSaving,
  error,
  onCancel,
  onSubmit,
}: {
  timezone: string;
  loggedInUserUuid: string;
  userScopes: string[];
  isSaving: boolean;
  error?: unknown;
  onCancel: () => void;
  onSubmit: (input: ManualNovaScheduleCreateInput) => Promise<void> | void;
}) {
  const [account, setAccount] = useState<SelectedAccount>(undefined);
  const [initialDraft] = useState(() => buildManualScheduleDraft(timezone));
  const [draft, setDraft] = useState<NovaScheduleProposalDraft>(initialDraft);
  const [promptFiles, setPromptFiles] = useState<NovaPromptFile[]>([]);
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  // Settings sit outside any chat: files upload under a chat of their own, committed on save.
  const [uploadChatUuid] = useState(() => v4());
  const mergedFiles = mergeNovaPromptFiles(promptFiles, attachments);
  const hasUnsettledAttachments = hasUnsettledNovaPromptAttachments(attachments);
  const validationError = validateNovaScheduleProposalDraft(draft);
  const canSubmit = !validationError && !isSaving && !hasUnsettledAttachments;
  useUnsavedNovaChanges(
    Boolean(account) ||
      JSON.stringify(draft) !== JSON.stringify(initialDraft) ||
      promptFiles.length > 0 ||
      attachments.length > 0,
  );

  const submit = () => {
    if (!canSubmit) return;
    void onSubmit({
      account,
      title: draft.title.trim(),
      prompt: draft.prompt.trim(),
      attachmentUploads: novaPromptFileAttachments(mergedFiles, readyUploads(attachments)).attachmentUploads,
      timezone: draft.timezone,
      recurrence: draft.recurrence,
      ...(draft.outputPolicy ? { outputPolicy: draft.outputPolicy } : {}),
      ...(draft.startAt ? { startAt: draft.startAt } : {}),
    });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-col gap-6 pb-8">
        <ManualScheduleField label="Account">
          <AccountSelector
            selectedAccount={account}
            onSelectAccount={setAccount}
            loggedInUserUuid={loggedInUserUuid}
            userScopes={userScopes}
            disabled={isSaving}
            placeholder={NO_ACCOUNT_SELECTED_SCHEDULE_LABEL}
            containerClassName="w-full sm:mx-0"
            className="h-[42px] w-full max-w-none sm:min-w-0"
          />
        </ManualScheduleField>

        <ManualScheduleField label="Briefing name" required htmlFor="nova-schedule-create-title">
          <Input
            fieldId="nova-schedule-create-title"
            aria-label="Briefing name"
            value={draft.title}
            maxLength={200}
            disabled={isSaving}
            onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
          />
        </ManualScheduleField>

        <ManualScheduleField label="What to include" required>
          <NovaPromptEditor
            value={draft.prompt}
            accountUuid={account?.uuid}
            existingFiles={promptFiles}
            attachments={attachments}
            disabled={isSaving}
            onChange={(prompt) => setDraft((current) => ({ ...current, prompt }))}
            onExistingFilesChange={setPromptFiles}
            setAttachments={setAttachments}
            chatUuid={uploadChatUuid}
          />
        </ManualScheduleField>

        {draft.recurrence.preset !== 'ONCE' ? (
          <ManualScheduleField label="Delivery">
            <NovaScheduleDeliveryEditor
              value={draft.outputPolicy}
              disabled={isSaving}
              onChange={(outputPolicy) => setDraft((current) => ({ ...current, outputPolicy }))}
            />
          </ManualScheduleField>
        ) : null}

        <ManualScheduleField label="Schedule" required>
          <div className="border-steel-75 bg-steel-50 rounded-[4px] border p-6">
            <NovaScheduleWhenEditor draft={draft} timezone={timezone} disabled={isSaving} onChange={setDraft} />
          </div>
        </ManualScheduleField>

        {validationError ? <p className="text-[13px] leading-5 text-[#C5342A]">{validationError}</p> : null}
        {error ? (
          <p className="text-[13px] leading-5 text-[#C5342A]">Unable to create this schedule. Try again.</p>
        ) : null}
      </div>

      <div className="border-steel-75 mt-auto flex gap-2.5 border-t bg-white pt-4">
        <Button type="button" variant="secondary" className="flex-1" disabled={isSaving} onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" className="flex-1" disabled={!canSubmit} onClick={submit}>
          {isSaving ? 'Saving...' : 'Save schedule'}
        </Button>
      </div>
    </div>
  );
}

function ManualScheduleField({
  label,
  required = false,
  htmlFor,
  children,
}: {
  label: string;
  required?: boolean;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={htmlFor} className="text-steel-500 text-[14px] leading-5 font-semibold">
        {label}
        {required ? '*' : ''}
      </label>
      {children}
    </div>
  );
}

function buildManualScheduleDraft(timezone: string): NovaScheduleProposalDraft {
  return {
    title: '',
    prompt: '',
    timezone,
    recurrence: { preset: 'WEEKLY', timeOfDay: '09:00', weekdays: ['MO'] },
    outputPolicy: 'APPEND_TO_CHAT',
  };
}
